import {WorkspaceReservationDto} from '@lib/infrastructure';
import {systemDateFormat} from '@lib/utils';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
  GroupReservationInviteeSearchResult,
  GroupWorkspaceReservation,
  ReservationMember,
  WorkspaceReservation,
} from '../../entities';
import {extractedUserDisplayName} from '../utils';

const getMembers = (nodeIds: string[], membersIds: string[], currentUserId: string): ReservationMember[] => {
  return membersIds.map((memberId, index) => {
    const nodeId = nodeIds.length === 1 ? nodeIds[0] : nodeIds[index];
    const isOwner = memberId === currentUserId;
    return {userId: memberId, nodeId, isOwner};
  }) as ReservationMember[];
};

export const toWorkspaceReservationDto = (
  currentUserId: string,
  nodeIds: string[],
  selectedDate: string,
  workspaceReservationId?: string,
  invitees?: GroupReservationInviteeSearchResult[],
): WorkspaceReservationDto => {
  const membersIds = [currentUserId, ...(invitees?.map((invitee) => invitee.id) || [])];
  const members = getMembers(nodeIds, membersIds, currentUserId);
  return {
    id: workspaceReservationId,
    nodeId: nodeIds[0],
    startDate: format(parseISO(selectedDate), systemDateFormat),
    endDate: format(parseISO(selectedDate), systemDateFormat),
    members,
  } as WorkspaceReservationDto;
};

export const toWorkspaceReservation = (
  {members, ...rest}: WorkspaceReservationDto,
  userId?: string,
): WorkspaceReservation => {
  const _members = members.map(
    (member) =>
      ({
        ...member,
        user: {...member.user, displayName: extractedUserDisplayName(member.user)},
      } as ReservationMember),
  );

  return {
    ...rest,
    members: _members,
    currentMember: _members.find((member) => member.userId === userId) as ReservationMember,
    owner: _members.find((member) => member.isOwner) as ReservationMember,
    floorNumber: rest.floorNumber ?? 0,
    createdBy: rest.createdBy && (rest.createdBy.displayName || rest.createdBy.email || ''),
    createdOrLastModifiedDate: rest.createdBy && (rest.createdBy.createdOrLastModifiedDate || ''),
  };
};

export const toGroupWorkspaceReservationRequestData = (
  nodeIds: string[],
  currentUserId: string,
  selectedInvitees: GroupReservationInviteeSearchResult[],
  selectedDate: string,
): GroupWorkspaceReservation => {
  const inviteeAndOwnerIds = [currentUserId, ...selectedInvitees.map((selectedInvitee) => selectedInvitee.id)];
  const {owner, invitees} = inviteeAndOwnerIds.reduce(
    (acc, currentItem, index) => {
      const nodeId = nodeIds.length === 1 ? nodeIds[0] : nodeIds[index];
      if (!acc.owner.nodeId) {
        return {
          ...acc,
          owner: {
            nodeId,
            userId: currentItem,
          },
        };
      }
      return {
        ...acc,
        invitees: [...acc.invitees, {nodeId, userId: currentItem}],
      };
    },
    {
      owner: {} as {nodeId: string; userId: string},
      invitees: [] as {nodeId: string; userId: string}[],
    },
  );
  return {
    startDate: format(parseISO(selectedDate), systemDateFormat),
    endDate: format(parseISO(selectedDate), systemDateFormat),
    invitees,
    owner,
  } as GroupWorkspaceReservation;
};
